import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

/**
 * Agent State Timeline (2026-08-10) — chronological state-change trace for ONE
 * agent (coaching, disputes, "what was Jane doing at 2pm?"). Requires an agent
 * to be selected. Source: agent_state_history.
 */
function fmtSecs(s) {
  s = Number(s) || 0;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`
              : `${m}:${String(r).padStart(2, '0')}`;
}
function fmtTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}
const STATE_LABEL = {
  ready: 'Ready', on_call: 'On call (in)', on_call_outbound: 'On call (out)',
  wrap: 'Wrap', aux: 'AUX', logout: 'Logged out',
};

export default function AgentStateTimeline() {
  const [range, setRange, apiParams] = useRangeState('today');
  const [filters, setFilters] = useState({});
  const agent = filters.agent || '';
  const merged = { ...apiParams, agent };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'agent-timeline', merged],
    queryFn: () => reportsApi.agentTimeline(merged),
    keepPreviousData: true,
    enabled: !!agent,   // don't fetch until an agent is chosen
  });

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">Agent state timeline</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="agent-state-timeline" params={merged} disabled={isLoading || !agent} />
          <button onClick={() => refetch()} disabled={!agent}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle disabled:opacity-40">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      {!agent && (
        <div className="text-sm text-surface-muted p-4 rounded-thin border border-surface-border dark:border-surface-dark-border">
          Select an agent above to view their state-change timeline.
        </div>
      )}

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      {agent && (
        <ReportTable
          headers={['entered_at','duration_sec','from_state','to_state','reason']}
          rows={(data?.rows || []).map(r => ({
            ...r,
            from_state: STATE_LABEL[r.from_state] || r.from_state || '—',
            to_state: STATE_LABEL[r.to_state] || r.to_state,
          }))}
          columnLabels={{
            entered_at: 'Time', duration_sec: 'Duration',
            from_state: 'From', to_state: 'To', reason: 'Reason',
          }}
          formatters={{ entered_at: fmtTime, duration_sec: fmtSecs }}
        />
      )}
    </div>
  );
}
